import { useVirtualKeyboard } from '@pages/TwinPage/hooks/useVirtualKeyboard';
import { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import { ExternalLinkIcon, LinkedInIcon } from '../../../../components/Icons';
import { useFetchLinkedInProfile } from '../../../../hooks/apiHooks/twinHooks';
import {
  useTwinData,
  useTwinPreview,
} from '../../../../hooks/storeHooks/useTwin';
import { Heading } from '../Heading';
import { InfoCard } from '../InfoCard';

interface LinkedInTabProps {
  openNextTab: () => void;
}

const LinkedInTab: React.FC<LinkedInTabProps> = memo(({ openNextTab }) => {
  const { t } = useTranslation();
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { setLinkedInProfile } = useTwinData();
  const { setPreviewTabName } = useTwinPreview();

  useVirtualKeyboard([inputRef]);

  const { mutateAsync: fetchLinkedInProfile, isPending } =
    useFetchLinkedInProfile();

  const validateLinkedInUrl = (url: string): string => {
    if (!url.trim()) {
      return t('twin.enterLinkedInUrl');
    }

    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      const isLinkedInDomain =
        hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com');

      if (!isLinkedInDomain) {
        return t('twin.invalidLinkedInUrl');
      }

      const pathname = urlObj.pathname.toLowerCase();
      if (!pathname.includes('/in/') && !pathname.includes('/pub/')) {
        return t('twin.invalidLinkedInProfile');
      }

      return '';
    } catch {
      return t('twin.invalidUrlFormat');
    }
  };

  const validateImageUrl = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok && response.status >= 200 && response.status < 300;
    } catch {
      return false;
    }
  };

  const handleContinue = async () => {
    const validationError = validateLinkedInUrl(linkedInUrl);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const data = await fetchLinkedInProfile({
        url: linkedInUrl,
      });
      if (!data.success) {
        throw new Error('Failed to fetch LinkedIn profile');
      }

      let isImageUrlValid = false;
      if (data.profileImageUrl) {
        isImageUrlValid = await validateImageUrl(data.profileImageUrl);
      }

      const profileDataToSave = {
        ...data,
        profileImageUrl: isImageUrlValid ? data.profileImageUrl : '',
      };

      if (isImageUrlValid) {
        setPreviewTabName('preview');
      }
      setLinkedInProfile(profileDataToSave);
      openNextTab();
    } catch (error) {
      console.error('Error fetching LinkedIn profile:', error);
      toast.error(t('twin.linkedInFetchFailed'));
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkedInUrl(e.target.value);
  };

  return (
    <div className="max-w-[600px] mb-auto sm:mb-0">
      <Heading
        title={t('twin.linkedInTitle')}
        subtitle={t('twin.linkedInSubtitle')}
      />
      <label className="text-white text-base font-normal leading-[150%] mb-2 block">
        {t('twin.pasteLinkedIn')}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id="linkedInUrl"
          type="url"
          value={linkedInUrl}
          onChange={handleUrlChange}
          placeholder={t('twin.linkedInPlaceholder')}
          className="px-4 pl-12 h-[44px] text-white w-full  border-dark-neutral-500 focus:border-coralborder focus:outline-none shadow-[0_-0.5px_1px_0_rgba(255,255,255,0.30)_inset,0_-0.5px_1px_0_rgba(255,255,255,0.25)_inset,1px_1.5px_4px_0_rgba(0,0,0,0.08)_inset,1px_1.5px_4px_0_rgba(0,0,0,0.10)_inset] bg-[rgb(128_128_128/30%)] border-t-[1px] border-t-[rgb(255_255_255/50%)] rounded-2xl transition-colors duration-200 bg-blend-luminosity backdrop-blur-[50px] [-webkit-text-fill-color:black !important]"
          disabled={isPending}
        />
        <LinkedInIcon
          size={21}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
        />
      </div>

      <div className="text-white/50 text-xs font-normal leading-[150%] flex align-middle !justify-start mt-2 gap-2">
        <a
          data-testid="linkedin-url-help-link"
          href="https://www.linkedin.com/in/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex align-middle shrink-0">
          {t('twin.getLinkedInUrl')}&nbsp;
          <ExternalLinkIcon size={12} />
        </a>
        <span className="bg-dark-neutral-400 w-[3px] h-[3px] rounded-full inline-block align-middle shrink-0" />
        <span className="shrink-0">{t('twin.readOnly')}</span>
      </div>

      <InfoCard
        title={t('twin.setupInSeconds')}
        description={t('twin.setupInSecondsDesc')}
        className="mt-8"
      />
      <InfoCard
        title={t('twin.realYouResponses')}
        description={t('twin.realYouResponsesDesc')}
        className="mb-6"
      />

      <div className="flex gap-4 justify-center mt-8">
        <button
          data-testid="linkedin-skip-btn"
          className="btn btn-secondary sm:!grow-0 !grow"
          onClick={openNextTab}
          disabled={isPending}>
          {t('twin.skipForNow')}
        </button>
        <button
          data-testid="linkedin-continue-btn"
          onClick={handleContinue}
          disabled={
            isPending ||
            !linkedInUrl.trim() ||
            !!validateLinkedInUrl(linkedInUrl)
          }
          className="btn btn-primary sm:!grow-0 !grow">
          {t('twin.continue')}
          {isPending ? (
            <AnimatedSpinner className="ml-2" />
          ) : (
            <i className="fa-solid fa-arrow-right ml-2" />
          )}
        </button>
      </div>
    </div>
  );
});

export { LinkedInTab };
